import { create } from 'zustand';
import apiClient from '@/services/apiClient';
import { supabase } from '@/config/supabase';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

export interface Property {
    id: string;
    owner_id: string;
    name: string;
    description: string;
    address: string;
    category: string;
    images: string[];
    latitude?: number;
    longitude?: number;
    unit_stats?: {
        total: number;
        occupied: number;
        available: number;
    };
    amenities?: string[];
    contact_email?: string;
    contact_phone?: string;
    created_at?: string;
}

export interface Unit {
    id: string;
    property_id: string;
    unit_number: string;
    rent_amount: number;
    status: 'Available' | 'Occupied' | 'Maintenance' | 'pending_approval';
    bedrooms: number;
    bathrooms: number;
    description?: string;
    unit_type?: string;
    created_at?: string;
    // Additional fields returned by backend
    current_tenant?: {
        id: string;
        full_name: string;
        avatar_url: string | null;
        phone_number: string | null;
        email: string | null;
        lease_start_date: string;
        lease_end_date: string | null;
        is_active: boolean;
    };
    has_pending_invitation?: boolean;
    pending_invitation_email?: string;
    amenities?: string[];
}

export interface Tenant {
    id: string;
    user_id: string;
    full_name: string;
    email: string;
    phone_number: string;
    unit_number?: string;
    property_name?: string;
    lease_start?: string;
    lease_end?: string;
    status: 'Active' | 'Past' | 'Pending';
}

export interface Invoice {
    id: string;
    tenant_name: string;
    unit_number: string;
    property_id?: string; // Kept as optional for frontend mapped data
    property_name?: string; // Kept as optional for frontend mapped data
    amount: number;
    amount_paid: number;
    balance: number;
    type: 'Rent' | 'Utility' | 'Service' | 'Other';
    status: 'Paid' | 'Pending' | 'Overdue' | 'Partially Paid';
    due_date: string;
    created_at: string;
}

export interface MaintenanceRequest {
    id: string;
    title: string;
    description: string;
    priority: 'Low' | 'Medium' | 'High' | 'Emergency';
    status: 'Pending' | 'In Progress' | 'Completed' | 'Cancelled';
    tenant_name: string;
    unit_number: string;
    property_name: string;
    property_id?: string;
    images?: string[];
    created_at: string;
}

export type BookingStatus = "Pending" | "Confirmed" | "Cancelled";
export interface ViewingRequest {
    id: string;
    tenantName: string;
    tenantAvatar: string;
    propertyName: string;
    unitName: string;
    requestedDate: string;
    requestedTime: string;
    status: BookingStatus;
}

export interface Vacancy {
    id: string;
    property_id: string;
    unit_id: string;
    title: string;
    description: string;
    rent_amount: number;
    available_date: string;
    status: 'Active' | 'Filled' | 'Draft';
    images: string[];
}

export interface Utility {
    id: string;
    property_id: string;
    name: string;
    type: 'Fixed' | 'Variable';
    cost: number;
    unit: string; // e.g., 'per month', 'per unit'
}

export type StaffRole = 'Property Manager' | 'Maintenance Staff' | 'Accountant' | 'Security' | 'Admin';
export type StaffStatus = 'Active' | 'Inactive' | 'Pending' | 'Suspended';

export interface LandlordStaff {
    id: string;
    name: string;
    role: StaffRole | string;
    phone: string;
    email?: string;
    status: StaffStatus | string;
    property_id?: string;
    property_name?: string;
    avatar_url?: string;
    created_at?: string;
    updated_at?: string;
}

export interface StaffInvitePayload {
    staffEmail: string;
    propertyId: string;
    role: StaffRole | string;
    compensationDetails?: {
        amount: number;
        currency: string;
        frequency: string;
    };
    permissions?: string[];
}

export interface StaffUpdatePayload {
    name?: string;
    role?: StaffRole | string;
    phone?: string;
    email?: string;
    status?: StaffStatus | string;
}

export interface StaffProfileDetail {
    id: string;
    profile: {
        name: string;
        profilePic: string;
        phone: string;
    };
    assignment: {
        role: string;
        status: string;
        assignedTo: string;
    };
    compensation: {
        amount?: number;
        currency?: string;
        frequency?: string;
        lastPaid?: string;
        [key: string]: any;
    };
    payrollSettings: {
        isAutomated: boolean;
        nextDueDate: string;
        paymentMethodId: string;
    };
    permissions: string[];
}

interface LandlordState {
    properties: Property[];
    currentProperty: Property | null;
    currentUnitDetails: any;
    units: Unit[];
    tenants: Tenant[];
    invoices: Invoice[];
    maintenanceRequests: MaintenanceRequest[];
    bookings: ViewingRequest[];
    vacancies: Vacancy[];
    utilities: Utility[];
    staff: LandlordStaff[];
    projects: Project[];
    loading: boolean;
    error: string | null;

    fetchProperties: () => Promise<void>;
    fetchPropertyDetails: (propertyId: string) => Promise<void>;
    fetchUnitDetails: (unitId: string) => Promise<void>;
    createProperty: (propertyData: Partial<Property>) => Promise<string>;
    updateProperty: (propertyId: string, propertyData: Partial<Property>) => Promise<void>;

    fetchUnits: (propertyId: string) => Promise<void>;
    createUnit: (propertyId: string, unitData: Partial<Unit>) => Promise<void>;

    fetchTenants: () => Promise<void>;
    fetchInvoices: () => Promise<void>;
    fetchMaintenanceRequests: () => Promise<void>;
    updateMaintenanceStatus: (requestId: string, status: string) => Promise<void>;

    fetchBookings: () => Promise<void>;
    updateBookingStatus: (bookingId: string, status: BookingStatus) => Promise<void>;
    fetchVacancies: () => Promise<void>;
    createVacancy: (vacancyData: any) => Promise<void>;
    updateVacancy: (vacancyId: string, vacancyData: any) => Promise<void>;
    deleteVacancy: (vacancyId: string) => Promise<void>;
    fetchUtilities: (propertyId: string) => Promise<void>;
    billUtility: (propertyId: string, utilityType: string, target: 'All' | 'Specific', unitId: string | undefined, amount: number, dueDate: string) => Promise<void>;

    // Staff Methods
    fetchStaff: (propertyId: string) => Promise<void>;
    addStaff: (payload: StaffInvitePayload) => Promise<void>;
    updateStaff: (assignmentId: string, payload: StaffUpdatePayload) => Promise<void>;
    deleteStaff: (assignmentId: string) => Promise<void>;
    fetchStaffDetails: (assignmentId: string) => Promise<StaffProfileDetail>;
    updateStaffPermissions: (assignmentId: string, permissions: string[]) => Promise<void>;
    updateStaffPayroll: (assignmentId: string, payrollData: any) => Promise<void>;
    updateStaffStatus: (assignmentId: string, status: StaffStatus | string) => Promise<void>;

    inviteTenant: (invitationData: TenantInvitation) => Promise<void>;
    updateTenantProfile: (tenantId: string, profileData: Partial<Tenant>) => Promise<void>;
    
    // Invitation tracking
    invitations: TrackedInvitation[];
    fetchInvitations: () => Promise<void>;
    revokeInvitation: (invitationId: string) => Promise<void>;
    resendInvitation: (invitationId: string) => Promise<void>;

    // Projects Methods
    fetchProjects: () => Promise<void>;
    fetchProjectDetails: (projectId: string) => Promise<void>;
    createProject: (projectData: Partial<Project>) => Promise<void>;
    updateProject: (projectId: string, projectData: Partial<Project>) => Promise<void>;
    deleteProject: (projectId: string) => Promise<void>;
    updateProjectStatus: (projectId: string, status: ProjectStatus) => Promise<void>;
    updateProjectProgress: (projectId: string, progress: number) => Promise<void>;
    addProjectExpense: (projectId: string, expenseData: Omit<ProjectExpense, 'id'>) => Promise<void>;
    addProjectPhoto: (projectId: string, photoData: Omit<ProjectPhoto, 'id' | 'uploaded_at'>) => Promise<void>;
    
    fetchAllLandlordData: () => Promise<void>;
}

export interface TenantInvitation {
    tenantEmail: string;
    unitId: string;
    leaseStartDate: string;
    rentAmount: number;
    securityDeposit: number;
    utilitiesResponsibilities?: string;
    houseRules?: string;
}

export interface TrackedInvitation {
    id: string;
    tenant_email: string;
    status: 'pending' | 'accepted' | 'declined' | 'expired' | 'revoked';
    created_at: string;
    expires_at?: string;
    unit_id: string;
    lease_start_date: string;
    rent_amount: number;
    security_deposit_amount: number;
    utilities_responsibilities?: Record<string, string>;
    house_rules?: string;
    units?: {
        id: string;
        unit_number: string;
    };
    properties?: {
        id: string;
        name: string;
        address?: string;
    };
    landlord?: {
        id: string;
        full_name: string;
    };
    accepted_at?: string;
    declined_at?: string;
}

// Project Types for Capital Projects & Renovations
export type ProjectStatus = 'Planned' | 'In Progress' | 'Completed' | 'On Hold' | 'Cancelled';
export type ProjectType = 'Renovation' | 'Repair' | 'Construction' | 'Maintenance' | 'Improvement';
export type ProjectPriority = 'Low' | 'Medium' | 'High' | 'Urgent';

export interface ProjectContractor {
    id: string;
    name: string;
    phone: string;
    email?: string;
    company?: string;
}

export interface ProjectExpense {
    id: string;
    description: string;
    amount: number;
    date: string;
    category: string;
    receipt_url?: string;
}

export interface ProjectPhoto {
    id: string;
    url: string;
    caption?: string;
    type: 'before' | 'during' | 'after';
    uploaded_at: string;
}

export interface Project {
    id: string;
    property_id: string;
    property_name?: string;
    name: string;
    description: string;
    type: ProjectType;
    status: ProjectStatus;
    priority: ProjectPriority;
    budget_estimated: number;
    budget_actual: number;
    start_date: string;
    end_date?: string;
    actual_completion_date?: string;
    contractor?: ProjectContractor;
    expenses: ProjectExpense[];
    photos: ProjectPhoto[];
    progress_percentage: number;
    notes?: string;
    created_at: string;
    updated_at: string;
}

export const useLandlordStore = create<LandlordState>((set, get) => ({
    properties: [],
    currentProperty: null,
    currentUnitDetails: null as any,
    units: [],
    projects: [],
    loading: false,
    error: null,

    fetchProperties: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_PROPERTIES);
            // Backend might return image_urls, map to images interface
            const properties = response.data.map((p: any) => ({
                ...p,
                images: p.images || p.image_urls || []
            }));
            set({ properties, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch properties:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch properties', loading: false });
        }
    },

    fetchPropertyDetails: async (propertyId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(`/properties/${propertyId}`);
            const data = response.data;
            
            let unitStats = data.unit_stats || { total: 0, occupied: 0, available: 0, pending: 0, occupancy_rate: 0 };
            if (!data.unit_stats && data.units && data.units.length > 0) {
                // Fallback computation
                unitStats.total = data.units[0].count || 0;
            }
            
            const propertyWithImages = {
                ...data,
                images: data.images || data.image_urls || [],
                unit_stats: unitStats
            };
            set({ currentProperty: propertyWithImages as Property, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch property details:', error);
            set({ error: error.message || 'Failed to fetch property details', loading: false });
        }
    },

    fetchUnitDetails: async (unitId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(`/units/${unitId}/details`);
            set({ currentUnitDetails: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch unit details:', error);
            set({ error: error.message || 'Failed to fetch unit details', loading: false });
        }
    },

    createProperty: async (propertyData: Partial<Property>) => {
        set({ loading: true, error: null });
        try {
            // 1. Create property via API (handles Landlord role promotion)
            // The backend doesn't handle images, so we strip them for the API call
            const { images, ...apiPayload } = propertyData;
            const response = await apiClient.post(API_ENDPOINTS.LANDLORD_PROPERTIES, apiPayload);
            const newPropertyId = response.data.id;

            // 2. If images exist, update them directly via Supabase
            if (images && images.length > 0) {
                const { error: imageError } = await supabase
                    .from('properties')
                    .update({ image_urls: images })
                    .eq('id', newPropertyId);

                if (imageError) {
                    console.error('Failed to save property images:', imageError);
                    // We don't throw here to avoid failing the whole creation, 
                    // but we might want to notify the user
                }
            }

            await get().fetchProperties();
            set({ loading: false });
            return newPropertyId;
        } catch (error: any) {
            console.error('Failed to create property:', error);
            set({ error: error.response?.data?.error || 'Failed to create property', loading: false });
            throw error;
        }
    },

    updateProperty: async (propertyId: string, propertyData: Partial<Property>) => {
        set({ loading: true, error: null });
        try {
            // 1. Update basic details via API
            const { images, ...apiPayload } = propertyData;
            await apiClient.patch(API_ENDPOINTS.LANDLORD_UPDATE_PROPERTY(propertyId), apiPayload);

            // 2. Update images directly via Supabase if included
            if (images) {
                const { error: imageError } = await supabase
                    .from('properties')
                    .update({ image_urls: images })
                    .eq('id', propertyId);

                if (imageError) console.error('Failed to update property images:', imageError);
            }

            await get().fetchProperties();
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to update property:', error);
            set({ error: error.response?.data?.error || 'Failed to update property', loading: false });
            throw error;
        }
    },

    fetchUnits: async (propertyId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(`/units/property/${propertyId}`);
            const data = response.data;
            
            const formattedUnits = data.map((unit: any) => {
                let extractedTenant = null;
                if (unit.tenants && unit.tenants.length > 0 && unit.tenants[0].profiles) {
                    extractedTenant = unit.tenants[0].profiles;
                }
                
                return {
                    ...unit,
                    current_tenant: extractedTenant,
                    has_pending_invitation: unit.has_pending_invitation || false,
                    pending_invitation_email: unit.pending_invitation?.tenant_email || null,
                };
            });
            
            set({ units: formattedUnits as Unit[], loading: false });
        } catch (error: any) {
            console.error('Failed to fetch units:', error);
            set({ error: error.message || 'Failed to fetch units', loading: false });
        }
    },

    createUnit: async (propertyId: string, unitData: Partial<Unit>) => {
        set({ loading: true, error: null });
        try {
            // BACKEND IMPLEMENTATION (Per User Request)
            // Note: This endpoint does NOT support bedrooms, bathrooms, or description.
            // Data for those fields will be lost.

            const payload = {
                propertyId,
                unitName: unitData.unit_number,
                unitType: unitData.unit_type || 'Residential',
                status: unitData.status || 'Available',
                baseRent: unitData.rent_amount,
                amenities: [] // We don't have amenities in the simple form yet
            };

            await apiClient.post('/units', payload);

            // Refresh units to get the new one with its ID from the backend
            await get().fetchUnits(propertyId);
            await get().fetchProperties(); // Refresh property stats
            set({ loading: false });

        } catch (error: any) {
            console.error('Error in createUnit:', error);
            set({ error: error.message || 'Failed to create unit', loading: false });
            throw error;
        }
    },

    // --- Tenants ---
    tenants: [],
    invitations: [],
    fetchTenants: async () => {
        set({ loading: true, error: null });
        try {
            // Fetch all tenants for landlord's properties
            // We might need a specific endpoint for this if not already available
            // Based on routes/tenants.js: router.get('/for-selection', ...) seem to get all relevant tenants
            const response = await apiClient.get('/tenants/for-selection');
            set({ tenants: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch tenants:', error);
            // Optionally don't set global error if just one part fails
            set({ loading: false });
        }
    },

    inviteTenant: async (invitationData: TenantInvitation) => {
        set({ loading: true, error: null });
        try {
            await apiClient.post('/tenants/invite', invitationData);
            // Refresh tenants after invite so user sees the new record (or pending tenant placeholder in list)
            await get().fetchTenants();
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to invite tenant:', error);
            set({ error: error.response?.data?.error || 'Failed to invite tenant', loading: false });
            throw error;
        }
    },

    updateTenantProfile: async (tenantId: string, profileData: Partial<Tenant>) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(`/tenants/profile/${tenantId}`, profileData);
            // Refresh the local list and profile screen if needed
            await get().fetchTenants();
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to update tenant profile:', error);
            set({ error: error.response?.data?.error || 'Failed to update tenant profile', loading: false });
            throw error;
        }
    },

    // --- Invitation Tracking ---
    fetchInvitations: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get('/tenants/invitations/pending');
            set({ invitations: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch invitations:', error);
            set({ error: error.response?.data?.error || 'Failed to fetch invitations', loading: false });
        }
    },

    revokeInvitation: async (invitationId: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(`/tenants/invitations/${invitationId}`);
            // Remove from local state
            set((state) => ({
                invitations: state.invitations.filter(inv => inv.id !== invitationId),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to revoke invitation:', error);
            set({ error: error.response?.data?.error || 'Failed to revoke invitation', loading: false });
            throw error;
        }
    },

    resendInvitation: async (invitationId: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.post(`/tenants/invitations/${invitationId}/resend`);
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to resend invitation:', error);
            set({ error: error.response?.data?.error || 'Failed to resend invitation', loading: false });
            throw error;
        }
    },

    // --- Invoices / Financials ---
    invoices: [],
    fetchInvoices: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get('/invoicing');

            // Map to store Invoice interface
            const formattedInvoices: Invoice[] = response.data.map((inv: any) => ({
                id: inv.id,
                tenant_name: inv.tenantName,
                unit_number: inv.unitName,
                unit_number_alias: inv.unitName, // Add compatibility field if needed
                amount: inv.amount,
                amount_paid: inv.amount - inv.balance,
                balance: inv.balance,
                type: 'Rent', // Defaulting to Rent since backend doesn't return type in list
                status: inv.status,
                due_date: inv.dueDate,
                created_at: inv.dueDate // Fallback since created_at is not returned
            }));

            set({ invoices: formattedInvoices, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch invoices:', error);
            set({ loading: false });
        }
    },

    // --- Maintenance ---
    maintenanceRequests: [],
    fetchMaintenanceRequests: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_MAINTENANCE);

            // The backend returns an array of objects.
            // Map the joined fields to match the frontend MaintenanceRequest interface.
            const mappedRequests = response.data.map((req: any) => ({
                id: req.id,
                title: req.category, // Using category as the title
                description: req.description,
                priority: 'Medium', // Or map if your backend adds priority later
                status: req.status,
                tenant_name: req.tenant?.full_name || 'Unknown Tenant',
                unit_number: req.units?.unit_number || 'Unknown Unit',
                property_name: req.units?.properties?.name || 'Unknown Property',
                property_id: req.property_id, // Useful for filtering later
                images: req.photos || [],
                created_at: req.created_at
            }));

            set({ maintenanceRequests: mappedRequests, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch maintenance requests:', error);
            set({ loading: false });
        }
    },

    updateMaintenanceStatus: async (requestId: string, status: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.put(API_ENDPOINTS.LANDLORD_UPDATE_MAINTENANCE(requestId), { status });

            // Update the local state to avoid refetching everything
            const currentRequests = get().maintenanceRequests;
            const updatedRequests = currentRequests.map(req =>
                req.id === requestId ? { ...req, status: status as any } : req
            );

            set({ maintenanceRequests: updatedRequests, loading: false });
        } catch (error: any) {
            console.error('Failed to update maintenance status:', error);
            set({ error: error.message || 'Failed to update status', loading: false });
            throw error; // Let the UI catch it if needed
        }
    },

    // --- Bookings ---
    bookings: [],
    fetchBookings: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get('/bookings');
            set({ bookings: response.data as ViewingRequest[], loading: false });
        } catch (error: any) {
            console.error('Failed to fetch viewing requests:', error);
            set({ error: error.message || 'Failed to fetch viewing requests', loading: false });
        }
    },

    // --- Vacancies ---
    vacancies: [],

    updateBookingStatus: async (bookingId: string, status: BookingStatus) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(`/bookings/${bookingId}/status`, { status });
            set((state) => ({
                bookings: state.bookings.map(b => 
                    b.id === bookingId ? { ...b, status } : b
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error(`Failed to update booking status:`, error);
            set({ error: error.message || 'Failed to update booking status', loading: false });
        }
    },

    fetchVacancies: async () => {
        set({ loading: true, error: null });
        try {
            // DIRECT SUPABASE CALL - Backend endpoint /api/vacancies/landlord does not exist
            // DIRECT SUPABASE CALL - Split query to avoid RLS recursion on 'tenants' relation
            // 1. Fetch vacancies without joins
            const { data: vacanciesData, error: vacanciesError } = await supabase
                .from('vacancies')
                .select('*')
                .eq('landlord_id', (await supabase.auth.getUser()).data.user?.id)
                .order('created_at', { ascending: false });

            if (vacanciesError) throw vacanciesError;

            if (!vacanciesData || vacanciesData.length === 0) {
                set({ vacancies: [], loading: false });
                return;
            }

            // 2. Fetch related Properties
            const propertyIds = [...new Set(vacanciesData.map((v: any) => v.property_id))];
            const { data: propertiesData, error: propertiesError } = await supabase
                .from('properties')
                .select('id, name')
                .in('id', propertyIds);

            if (propertiesError) console.error('Error fetching vacancy properties', propertiesError);

            // 3. Fetch related Units
            const unitIds = [...new Set(vacanciesData.map((v: any) => v.unit_id))];
            const { data: unitsData, error: unitsError } = await supabase
                .from('units')
                .select('id, unit_number')
                .in('id', unitIds);

            if (unitsError) console.error('Error fetching vacancy units', unitsError);

            // 4. Map data together
            const propertyMap = new Map(propertiesData?.map((p: any) => [p.id, p]));
            const unitMap = new Map(unitsData?.map((u: any) => [u.id, u]));

            const formattedVacancies = vacanciesData.map((v: any) => ({
                ...v,
                images: v.image_urls || [],
                property_name: propertyMap.get(v.property_id)?.name || 'Unknown Property',
                unit_name: unitMap.get(v.unit_id)?.unit_number || 'Unknown Unit'
            }));

            set({ vacancies: formattedVacancies, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch vacancies:', error);
            // Fallback to empty
            set({ vacancies: [], loading: false });
        }
    },

    createVacancy: async (vacancyData: any) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post('/vacancies', vacancyData);
            set((state) => ({ vacancies: [response.data, ...state.vacancies], loading: false }));
        } catch (error: any) {
            console.error('Failed to create vacancy:', error);
            set({ error: error.message || 'Failed to create vacancy.', loading: false });
            throw error;
        }
    },

    updateVacancy: async (vacancyId: string, vacancyData: any) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.patch(`/vacancies/${vacancyId}`, vacancyData);
            set((state) => ({
                vacancies: state.vacancies.map(v => 
                    v.id === vacancyId ? { ...v, ...response.data } : v
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update vacancy:', error);
            set({ error: error.message || 'Failed to update vacancy.', loading: false });
            throw error;
        }
    },

    deleteVacancy: async (vacancyId: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(`/vacancies/${vacancyId}`);
            set((state) => ({
                vacancies: state.vacancies.filter(v => v.id !== vacancyId),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to delete vacancy:', error);
            set({ error: error.message || 'Failed to delete vacancy.', loading: false });
            throw error;
        }
    },

    // --- Utilities ---
    utilities: [],
    fetchUtilities: async (propertyId: string) => {
        // The utilities table does not exist in the Supabase schema yet.
        // We will just bypass this fetch to avoid the 404/42P01 error on load.
        // Real utility Generation happens dynamically via the UtilityBillingModal anyway.
        set({ utilities: [], loading: false, error: null });
    },

    billUtility: async (propertyId: string, utilityType: string, target: 'All' | 'Specific', unitId: string | undefined, amount: number, dueDate: string) => {
        set({ loading: true, error: null });
        try {
            const tenants = get().tenants;
            const units = get().units;

            // We need to find units that belong to this property to filter tenants
            const propertyUnits = units.filter(u => u.property_id === propertyId);
            const propertyUnitNumbers = propertyUnits.map(u => u.unit_number);

            // Step 1: Filter active tenants for the property
            let targetTenants = tenants.filter(t =>
                t.status === 'Active' &&
                t.unit_number &&
                propertyUnitNumbers.includes(t.unit_number)
            );

            // Step 2: If targeting a specific unit, refine the list
            if (target === 'Specific' && unitId) {
                const specificUnit = units.find(u => u.id === unitId);
                if (specificUnit) {
                    targetTenants = targetTenants.filter(t => t.unit_number === specificUnit.unit_number);
                }
            }

            if (targetTenants.length === 0) {
                throw new Error("No active tenants found to bill for this selection.");
            }

            // Step 3: Loop through tenants and fire manual invoice creation
            const invoicePromises = targetTenants.map(tenant => {
                // Determine unit ID for this tenant. 
                const unitMatch = propertyUnits.find(u => u.unit_number === tenant.unit_number);
                const actualUnitId = unitMatch ? unitMatch.id : unitId || '';

                const lineItems = [{
                    description: utilityType,
                    amount: amount
                }];

                return apiClient.post('/invoicing', {
                    tenantId: tenant.user_id || tenant.id, // backend uses user_id generally, but fallback to id
                    unitId: actualUnitId,
                    propertyId: propertyId,
                    dueDate: dueDate,
                    lineItems: lineItems,
                    paymentInstructionId: null // Optional
                });
            });

            await Promise.all(invoicePromises);

            // Refetch invoices to see the new bills
            await get().fetchInvoices();
            set({ loading: false });
        } catch (error: any) {
            console.error("Failed to bill utility:", error);
            set({ error: error.message || 'Failed to bill utilities', loading: false });
            throw error;
        }
    },

    // --- Staff ---
    staff: [],
    fetchStaff: async (propertyId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_STAFF(propertyId));
            set({ staff: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch staff:', error);
            set({ staff: [], loading: false });
        }
    },

    addStaff: async (payload: any) => {
        set({ loading: true, error: null });
        try {
            await apiClient.post(API_ENDPOINTS.LANDLORD_STAFF_INVITE, payload);
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to add staff:', error);
            set({ error: error.response?.data?.error || 'Failed to add staff', loading: false });
            throw error;
        }
    },

    fetchStaffDetails: async (assignmentId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_STAFF_PROFILE(assignmentId));
            set({ loading: false });
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch staff details:', error);
            set({ error: error.message || 'Failed to load staff details.', loading: false });
            throw error;
        }
    },

    updateStaffPermissions: async (assignmentId: string, permissions: string[]) => {
        set({ loading: true, error: null });
        try {
            await apiClient.put(API_ENDPOINTS.LANDLORD_STAFF_UPDATE_PERMISSIONS(assignmentId), { permissions });
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to update staff permissions:', error);
            set({ error: error.message || 'Failed to update permissions.', loading: false });
            throw error;
        }
    },

    updateStaffPayroll: async (assignmentId: string, payrollData: any) => {
        set({ loading: true, error: null });
        try {
            await apiClient.put(API_ENDPOINTS.LANDLORD_STAFF_UPDATE_PAYROLL(assignmentId), payrollData);
            set({ loading: false });
        } catch (error: any) {
            console.error('Failed to update staff payroll settings:', error);
            set({ error: error.message || 'Failed to update payroll parameters.', loading: false });
            throw error;
        }
    },

    updateStaff: async (assignmentId: string, payload: StaffUpdatePayload) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(API_ENDPOINTS.LANDLORD_STAFF_UPDATE(assignmentId), payload);
            // Update local state
            const currentStaff = get().staff;
            const updatedStaff = currentStaff.map(s => 
                s.id === assignmentId ? { ...s, ...payload } : s
            );
            set({ staff: updatedStaff, loading: false });
        } catch (error: any) {
            console.error('Failed to update staff:', error);
            set({ error: error.response?.data?.error || 'Failed to update staff', loading: false });
            throw error;
        }
    },

    deleteStaff: async (assignmentId: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.LANDLORD_STAFF_DELETE(assignmentId));
            // Remove from local state
            const currentStaff = get().staff;
            set({ staff: currentStaff.filter(s => s.id !== assignmentId), loading: false });
        } catch (error: any) {
            console.error('Failed to delete staff:', error);
            set({ error: error.response?.data?.error || 'Failed to delete staff', loading: false });
            throw error;
        }
    },

    updateStaffStatus: async (assignmentId: string, status: StaffStatus | string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(API_ENDPOINTS.LANDLORD_STAFF_UPDATE_STATUS(assignmentId), { status });
            // Update local state
            const currentStaff = get().staff;
            const updatedStaff = currentStaff.map(s => 
                s.id === assignmentId ? { ...s, status } : s
            );
            set({ staff: updatedStaff, loading: false });
        } catch (error: any) {
            console.error('Failed to update staff status:', error);
            set({ error: error.response?.data?.error || 'Failed to update staff status', loading: false });
            throw error;
        }
    },

    // --- Projects ---
    fetchProjects: async () => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_PROJECTS);
            set({ projects: response.data, loading: false });
        } catch (error: any) {
            console.error('Failed to fetch projects:', error);
            set({ projects: [], loading: false });
        }
    },

    fetchProjectDetails: async (projectId: string) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.get(API_ENDPOINTS.LANDLORD_PROJECT_DETAILS(projectId));
            set({ loading: false });
            return response.data;
        } catch (error: any) {
            console.error('Failed to fetch project details:', error);
            set({ loading: false });
            throw error;
        }
    },

    createProject: async (projectData: Partial<Project>) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post(API_ENDPOINTS.LANDLORD_CREATE_PROJECT, projectData);
            set((state) => ({ projects: [response.data, ...state.projects], loading: false }));
        } catch (error: any) {
            console.error('Failed to create project:', error);
            set({ error: error.response?.data?.error || 'Failed to create project', loading: false });
            throw error;
        }
    },

    updateProject: async (projectId: string, projectData: Partial<Project>) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.patch(API_ENDPOINTS.LANDLORD_UPDATE_PROJECT(projectId), projectData);
            set((state) => ({
                projects: state.projects.map(p => 
                    p.id === projectId ? { ...p, ...response.data } : p
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update project:', error);
            set({ error: error.response?.data?.error || 'Failed to update project', loading: false });
            throw error;
        }
    },

    deleteProject: async (projectId: string) => {
        set({ loading: true, error: null });
        try {
            await apiClient.delete(API_ENDPOINTS.LANDLORD_DELETE_PROJECT(projectId));
            set((state) => ({
                projects: state.projects.filter(p => p.id !== projectId),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to delete project:', error);
            set({ error: error.response?.data?.error || 'Failed to delete project', loading: false });
            throw error;
        }
    },

    updateProjectStatus: async (projectId: string, status: ProjectStatus) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(API_ENDPOINTS.LANDLORD_UPDATE_PROJECT(projectId), { status });
            set((state) => ({
                projects: state.projects.map(p => 
                    p.id === projectId ? { ...p, status } : p
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update project status:', error);
            set({ error: error.message || 'Failed to update project status', loading: false });
            throw error;
        }
    },

    updateProjectProgress: async (projectId: string, progress: number) => {
        set({ loading: true, error: null });
        try {
            await apiClient.patch(API_ENDPOINTS.LANDLORD_UPDATE_PROJECT(projectId), { progress_percentage: progress });
            set((state) => ({
                projects: state.projects.map(p => 
                    p.id === projectId ? { ...p, progress_percentage: progress } : p
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to update project progress:', error);
            set({ error: error.message || 'Failed to update project progress', loading: false });
            throw error;
        }
    },

    addProjectExpense: async (projectId: string, expenseData: Omit<ProjectExpense, 'id'>) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post(`${API_ENDPOINTS.LANDLORD_PROJECT_DETAILS(projectId)}/expenses`, expenseData);
            set((state) => ({
                projects: state.projects.map(p => 
                    p.id === projectId ? { ...p, expenses: [...p.expenses, response.data] } : p
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to add project expense:', error);
            set({ error: error.message || 'Failed to add project expense', loading: false });
            throw error;
        }
    },

    addProjectPhoto: async (projectId: string, photoData: Omit<ProjectPhoto, 'id' | 'uploaded_at'>) => {
        set({ loading: true, error: null });
        try {
            const response = await apiClient.post(`${API_ENDPOINTS.LANDLORD_PROJECT_DETAILS(projectId)}/photos`, photoData);
            set((state) => ({
                projects: state.projects.map(p => 
                    p.id === projectId ? { ...p, photos: [...p.photos, response.data] } : p
                ),
                loading: false
            }));
        } catch (error: any) {
            console.error('Failed to add project photo:', error);
            set({ error: error.message || 'Failed to add project photo', loading: false });
            throw error;
        }
    },

    // Helper to load all dashboard data
    fetchAllLandlordData: async () => {
        set({ loading: true });
        try {
            // Use Promise.allSettled so that one failing endpoint (e.g. timeout) 
            // doesn't prevent the rest of the dashboard from loading
            const results = await Promise.allSettled([
                get().fetchProperties(),
                get().fetchTenants(),
                get().fetchInvoices(),
                get().fetchMaintenanceRequests(),
                get().fetchBookings(),
                get().fetchVacancies()
            ]);

            // Log any errors that occurred during the individual fetches
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    const endpoints = ['properties', 'tenants', 'invoices', 'maintenance', 'bookings', 'vacancies'];
                    console.error(`Failed to fetch ${endpoints[index]}:`, result.reason);
                }
            });

            set({ loading: false });
        } catch (error) {
            console.error('Error fetching dashboard data', error);
            set({ loading: false, error: 'Failed to load some dashboard data. Please refresh.' });
        }
    }
}));

